import CloseIcon from '@mui/icons-material/Close';
import { TabContext, TabList } from "@mui/lab";
import { Box, Container, Divider, IconButton, Stack, Tab, Tooltip, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classnames from "classnames";
import { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { WorkspacesContext } from "../../contexts/WorkspacesContext";
import useWorkspaces from "../../hooks/useWorkspaces";
import theme from '../../theme';
import InvestorsIconFC from "../atoms/InvestorsIconFC";
import UnassignedContentsBadge from "../atoms/UnassignedContentsBadge";
import GeneralSettings from "../modals/home/GeneralSettings";
import PlanModal from "../modals/subscriptions/PlanModal";
import configuredAsyncComponentLoader from "../templates/loader";

const ActivityFeed = configuredAsyncComponentLoader(() => import('../organisms/home/ActivityFeed'));

export enum HomePageTabs {
    Pipeline = 'Pipeline',
    Shared = 'Shared with you',
}

const useStyles = makeStyles((theme) => ({
    rootContainer: {
        height: '100%',
        maxWidth: 'unset !important',
        overflow: 'hidden',
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '2.2rem',
        fontWeight: '700',
    },
    tabs: {
        "& .MuiTabs-indicator": {
            height: 3,
            background: theme.colors.primary['700'],
        }
    },
    tab: {
        minWidth: 100,
        minHeight: 48,
        marginRight: 8,
        padding: '6px 8px',
    },
    tabText: {
        color: '#878E95',
        fontFamily: 'Lato',
        fontWeight: 'bold',
        textTransform: 'none',
    },
    selectedTabText: {
        color: 'black',
        fontFamily: 'Lato',
        fontWeight: 'bold',
    },
    tabPanel: {
        height: '100%',
        width: '100%',
        padding: 'unset',
    },
    mainText: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.colors.success['500'],
    },
    subText: {
        fontFamily: 'Lato',
        fontSize: '1rem',
        fontWeight: 500,
        color: '#666666',
    },
}));

const InvitationTooltip: React.FC<{
    instance?: boolean,
    children: JSX.Element,
    onCloseInstance?: () => void,
}> = ({ instance, onCloseInstance, children }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { pendingWorkspaces } = useContext(WorkspacesContext);
    const { fetchCurrentUserWorkspaceUserMemberships } = useWorkspaces();
    // eslint-disable-next-line
    const [openTooltip, setOpenTooltip] = useState<boolean|undefined>(false);

    useEffect(() => {
        if (userGroup)
            Promise.resolve(fetchCurrentUserWorkspaceUserMemberships());
        // eslint-disable-next-line
    }, [userGroup]);

    useEffect(() => {
        if (!pendingWorkspaces.length) {
            setOpenTooltip(false);
        } else {
            const timeOut = setTimeout(() => {
                clearTimeout(timeOut);
                setOpenTooltip(instance);
            }, 1000);
        }
    // eslint-disable-next-line
    }, [pendingWorkspaces]);


    useEffect(() => {
        if (!instance)
            setOpenTooltip(false);
    // eslint-disable-next-line
    }, [instance]);

    return (<>
        <Tooltip open={false}
            // open={openTooltip}
            placement="bottom-start"
            title={
                <Stack direction="column" textAlign="center" width="100%" height="100%"
                    onClick={() => setOpenTooltip(false)}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" width="100%">
                        <IconButton size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onCloseInstance?.();
                            }}
                            sx={{ color: 'gray' }}
                            disableRipple>
                            <CloseIcon sx={{ width: 28, height: 28, }} />
                        </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" width="100%" mt="-24px !important" pr={4}>
                        <InvestorsIconFC width={26} height={26} color={theme.colors.success['500']} />
                        <Typography className={classes.mainText}>
                            {`New invite${pendingWorkspaces.length > 1 ? 's' : ''}`}
                        </Typography>
                    </Stack>
                    <Typography className={classes.subText} mt={1}>
                        {'You have new deal'}<br/>
                        {'sharing invites pending'}
                    </Typography>
                </Stack>
            }
            componentsProps={{ tooltip: { sx: {
                position: 'relative',
                width: 230,
                height: 115,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                border: '2px solid #7bd4d4',
                background: 'white',
                boxShadow: `0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)`,
                transform: 'translateY(16px) !important',
                "&::before": {
                    content: '""',
                    position: 'absolute',
                    top: '-20px',
                    left: '23px',
                    width: '36px',
                    height: '20px',
                    backgroundColor: '#7bd4d4',
                    clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
                    zIndex: 0,
                },
                "&::after": {
                    content: '""',
                    position: 'absolute',
                    top: '-17px',
                    left: '26px',
                    width: '30px',
                    height: '18px',
                    backgroundColor: 'white',
                    clipPath: 'polygon(0 100%, 50% 0, 100% 100%)',
                    zIndex: 1,
                }
            }}}}>
            {children}
        </Tooltip>
    </>);
}

const HomePage: React.FC<{}> = () => {
    const classes = useStyles();
    const [selectedTab, setSelectedTab] = useState<HomePageTabs>(HomePageTabs.Pipeline);
    const [instance, setInstance] = useState<boolean>(true);

    const getClassForText = useCallback((tab: HomePageTabs) =>
        (classnames(classes.tabText, selectedTab === tab && classes.selectedTabText))
        // eslint-disable-next-line
    , [selectedTab]);

    useEffect(() => {
        if (selectedTab === HomePageTabs.Shared)
            setInstance(false);
    }, [selectedTab]);

    return (<>
        <Container className={classes.rootContainer}>
            <TabContext value={selectedTab}>
                <Stack direction="column" pt={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start">
                            <Typography className={classes.title}>
                                {'Welcome to your pipeline'}
                            </Typography>
                            <TabList className={classes.tabs} onChange={(_, value) => setSelectedTab(value)}>
                                <Tab className={classes.tab}
                                    label={<Typography className={getClassForText(HomePageTabs.Pipeline)}>
                                            {HomePageTabs.Pipeline}
                                        </Typography>}
                                    value={HomePageTabs.Pipeline} />
                                <Box display="none">
                                    <Tab className={classes.tab}
                                        label={<InvitationTooltip instance={instance} onCloseInstance={() => setInstance(false)}>
                                                <Typography className={getClassForText(HomePageTabs.Shared)}>
                                                    {HomePageTabs.Shared}
                                                </Typography>
                                            </InvitationTooltip>}
                                        value={HomePageTabs.Shared} />
                                </Box>
                            </TabList>
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="flex-end">
                            <UnassignedContentsBadge />
                            <GeneralSettings />
                        </Stack>
                    </Stack>
                    <Divider />
                </Stack>
                <ActivityFeed />
            </TabContext>
        </Container>
        <PlanModal variant="limit" />
    </>);
}

export default HomePage;
